/**
 * scoring.jsx
 * -----------
 * Geometry helpers to evaluate a stroke against an "arrow-up" guide.
 *
 * Export
 * - evaluateArrowUp(strokePts, guide, overlay, opts) → { pass, score }
 */

const DEFAULTS = {
  matchThreshold: 0.75,
  minLengthRatio: 0.18,
  minUpDeltaRatio: 0.05,
};

export function evaluateArrowUp(strokePts, guide, overlay, opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };
  const tol = Math.max(8, (overlay?.h ?? 0) * 0.04);

  const segs = [
    [guide.start, guide.mid],
    [guide.mid, guide.end],
  ];

  let inside = 0;
  for (const p of strokePts) {
    const d1 = pointToSegDist(p, segs[0][0], segs[0][1]);
    const d2 = pointToSegDist(p, segs[1][0], segs[1][1]);
    if (Math.min(d1, d2) <= tol) inside++;
  }
  const score = inside / Math.max(1, strokePts.length);

  const lengthOK = totalLength(strokePts) > (overlay?.w ?? 0) * cfg.minLengthRatio;
  const upwardOK =
    strokePts[strokePts.length - 1].y <
    strokePts[0].y - (overlay?.h ?? 0) * cfg.minUpDeltaRatio;

  return { pass: score >= cfg.matchThreshold && lengthOK && upwardOK, score };
}

function pointToSegDist(p, a, b) {
  const vx = b.x - a.x, vy = b.y - a.y;
  const wx = p.x - a.x, wy = p.y - a.y;
  const c1 = vx * wx + vy * wy;
  if (c1 <= 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const c2 = vx * vx + vy * vy;
  if (c2 <= c1) return Math.hypot(p.x - b.x, p.y - b.y);
  const t = c1 / c2;
  const proj = { x: a.x + t * vx, y: a.y + t * vy };
  return Math.hypot(p.x - proj.x, p.y - proj.y);
}

function totalLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  return len;
}
